import type { SetBLdMotivationKey } from '@shared/constants/setBLdMotivation';
import { SET_B_LD_MOTIVATION_KEYS } from '@shared/constants/setBLdMotivation';

/** localStorage draft for Set B /ldproductnow. */
export const LD_PRODUCT_NOW_STORAGE_KEY = 'ldsurvey:ldProductNow';

export type LdMotivationKey = SetBLdMotivationKey;

export type LdMotivations = Record<LdMotivationKey, boolean>;

export type LdProductNowAnswers = {
  variant: string;
  motivations: LdMotivations;
  elaboration: string;
  smokedOtherProduct: '' | 'yes' | 'no';
};

export const emptyLdMotivations = (): LdMotivations => ({
  taste: false,
  brandImage: false,
  productQuality: false,
  price: false,
  productFeatures: false,
  recommendedByOthers: false,
  mentholFlavorExperience: false,
});

export const emptyLdProductNowAnswers = (): LdProductNowAnswers => ({
  variant: '',
  motivations: emptyLdMotivations(),
  elaboration: '',
  smokedOtherProduct: '',
});

function parseMotivations(value: unknown): LdMotivations {
  const empty = emptyLdMotivations();
  if (typeof value !== 'object' || value === null) return empty;
  const o = value as Record<string, unknown>;
  return {
    taste: o.taste === true || o.strongTaste === true,
    brandImage: o.brandImage === true,
    productQuality: o.productQuality === true,
    price: o.price === true,
    productFeatures: o.productFeatures === true,
    recommendedByOthers: o.recommendedByOthers === true || o.peerRecommendation === true,
    mentholFlavorExperience: o.mentholFlavorExperience === true,
  };
}

export function readLdProductNowAnswers(): LdProductNowAnswers {
  try {
    const raw = localStorage.getItem(LD_PRODUCT_NOW_STORAGE_KEY);
    if (!raw) return emptyLdProductNowAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyLdProductNowAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';

    let motivations = emptyLdMotivations();

    if (o.motivations != null && typeof o.motivations === 'object') {
      motivations = parseMotivations(o.motivations);
    } else if (typeof o.motivation === 'string' && isMotivationKey(o.motivation)) {
      motivations = { ...emptyLdMotivations(), [o.motivation]: true };
    }

    return {
      variant: typeof o.variant === 'string' ? o.variant : '',
      motivations,
      elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyLdProductNowAnswers();
  }
}

function isMotivationKey(value: string): value is LdMotivationKey {
  return (SET_B_LD_MOTIVATION_KEYS as readonly string[]).includes(value);
}

export function writeLdProductNowAnswers(data: LdProductNowAnswers): void {
  try {
    localStorage.setItem(LD_PRODUCT_NOW_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearLdProductNowAnswers(): void {
  try {
    localStorage.removeItem(LD_PRODUCT_NOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getSelectedLdMotivations(answers: LdProductNowAnswers): LdMotivationKey[] {
  return SET_B_LD_MOTIVATION_KEYS.filter((key) => answers.motivations[key]);
}

export function getLdMotivationDetail(answers: LdProductNowAnswers): string {
  return answers.elaboration.trim();
}

export function hasLdMotivationSelected(answers: LdProductNowAnswers): boolean {
  return getSelectedLdMotivations(answers).length > 0 && getLdMotivationDetail(answers).length > 0;
}

export function isLdProductNowComplete(): boolean {
  const data = readLdProductNowAnswers();
  if (!data.variant.trim()) return false;
  if (!hasLdMotivationSelected(data)) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export const LD_MOTIVATION_OPTION_KEYS = SET_B_LD_MOTIVATION_KEYS;
export const LD_MOTIVATION_KEYS = [...SET_B_LD_MOTIVATION_KEYS] as LdMotivationKey[];
